import { useMatches } from 'react-router-dom';

/**
 * Tipo de pantalla, declarado POR RUTA.
 *
 * Reemplaza a `FULL_BLEED_PREFIXES`, que resolvía con `startsWith` sobre una
 * lista de prefijos: marcar `/settings` habría arrastrado sus 8 subpantallas de
 * formulario sin que nadie lo pidiera. Con la declaración por ruta, cada
 * pantalla dice lo suyo y la más profunda gana.
 *
 * - `work`     · pantalla de trabajo (lista, tablero, consola, bandeja). Ocupa
 *                el alto de la ventana y desplaza POR DENTRO. La caja la da el
 *                shell: la pantalla solo aporta `flex-1 min-h-0`.
 * - `document` · documento o formulario. Ancho de lectura acotado, el
 *                navegador desplaza la página. Es el DEFAULT.
 * - `gesture`  · una sola acción o un solo mensaje (login, sin acceso, error,
 *                404). Tarjeta angosta centrada.
 */
export type ScreenLayout = 'work' | 'document' | 'gesture';

/** Lo que una ruta puede declarar en su `handle`. */
export interface RouteLayoutHandle {
  layout?: ScreenLayout;
}

const ES_LAYOUT = (v: unknown): v is ScreenLayout =>
  v === 'work' || v === 'document' || v === 'gesture';

/**
 * Resuelve el tipo de pantalla de una lista de matches de react-router.
 *
 * Gana **la declaración más profunda**: una ruta padre marcada `work` no
 * arrastra a una hija que se declara `document`. Sin declaración en ninguna,
 * devuelve `document`, que es el comportamiento que ya tenía la aplicación.
 *
 * Se exporta aparte del hook para poder testearla con un árbol simulado, sin
 * montar un router.
 */
export function resolverLayout(
  matches: ReadonlyArray<{ handle?: unknown }>,
): ScreenLayout {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const declarado = (matches[i]?.handle as RouteLayoutHandle | undefined)
      ?.layout;
    if (ES_LAYOUT(declarado)) return declarado;
  }
  return 'document';
}

/** Tipo de pantalla de la ruta activa. */
export function useScreenLayout(): ScreenLayout {
  return resolverLayout(useMatches());
}
